/**
 * DiscordConsole.jsx — Discord status + read-only command catalogue +
 * slash-command tester (#252, #309).
 *
 * Read path : /api/v2/widgets/discord-console (tier=user; backend
 *             filters command list by viewer role)
 * Write path:
 *   - Non-admin viewers → POST /api/v2/discord/test-command (allowlist
 *     enforced server-side, defence in depth)
 *   - Admin viewers     → POST /api/admin/test-command (existing v1
 *     route; full registry available)
 *
 * Validation: the tester only allows command names from the registry
 * payload returned by `fetch`; the form's <select> never accepts free-
 * form input. Both endpoints validate `commandName` server-side, and
 * the user-tier endpoint additionally rejects anything not in the
 * USER_SAFE_COMMANDS allowlist regardless of what the UI sends.
 *
 * Output sanitisation: the rendered output is the JSON the server
 * returns (content / embeds / data). We *do not* execute or eval any
 * of it; tokens / secrets aren't surfaced because the test endpoint
 * runs the command's `process()` function with a synthetic context
 * (no Discord client write side-effects).
 *
 * CSP: every handler is a Solid event listener.
 */
import { Show, For, createMemo, createSignal } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import toast from '../lib/toastStore.js';
import { EMOJI } from '../lib/emoji.js';

function statusColor(connected) {
  return connected ? 'var(--fissal, #6cf)' : 'var(--bad, #ff6b6b)';
}

export default function DiscordConsole() {
  const { data, loading, error, refetch } = useWidgetData('discord-console', () => ({}));

  const [filter, setFilter]       = createSignal('');
  const [selected, setSelected]   = createSignal('');
  const [args, setArgs]           = createSignal('');
  const [debug, setDebug]         = createSignal(false);
  const [running, setRunning]     = createSignal(false);
  const [result, setResult]       = createSignal(null);

  const status     = createMemo(() => data()?.status || {});
  const commands   = createMemo(() => data()?.commands || []);
  const channels   = createMemo(() => Object.entries(status().channels || {}));

  const filteredCmds = createMemo(() => {
    const q = filter().trim().toLowerCase();
    const list = commands();
    if (!q) return list;
    return list.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.description || '').toLowerCase().includes(q),
    );
  });

  const selectedMeta = createMemo(() =>
    commands().find(c => c.name === selected()) || null);

  async function runCommand() {
    if (running()) return;
    const name = selected();
    if (!name) { toast.error('Select a command first.'); return; }
    // Guard: ensure the chosen name is in the registry payload.
    if (!commands().some(c => c.name === name)) {
      toast.error('Unknown command (not in registry).');
      return;
    }
    setRunning(true); setResult(null);
    // Choose endpoint by viewer role. Non-admin → user-tier endpoint
    // that enforces USER_SAFE_COMMANDS allowlist server-side.
    const endpoint = data()?.isAdmin
      ? '/api/admin/test-command'
      : '/api/v2/discord/test-command';
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          commandName: name,
          args: args(),
          debug: debug() || undefined,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setResult(body);
    } catch (err) {
      toast.error('Test failed', { detail: err?.message });
    } finally {
      setRunning(false);
    }
  }

  return (
    <div class="v2-widget">
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__title">{EMOJI.bot} Discord Console</span>
        <button class="v2-widget-hdr__btn" style={{ 'margin-left': 'auto' }}
          title="Refresh" onClick={() => refetch()}>↻</button>
      </div>
      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <div class="v2-widget__scroll" style={{ padding: '0.5rem 0.75rem' }}>

            {/* Status panel */}
            <div style={{
              padding: '0.5rem 0.6rem',
              'margin-bottom': '0.6rem',
              border: '1px solid var(--border, rgba(255,255,255,0.08))',
              'border-radius': 'var(--radius, 6px)',
              background: 'rgba(0,0,0,0.18)',
            }}>
              <Show when={status().error}>
                <div class="v2-error">{status().error}</div>
              </Show>
              <Show when={!status().error}>
                <div style={{ display: 'flex', 'flex-wrap': 'wrap', gap: '0.5rem 1rem',
                  'font-size': 'var(--fs-xs)' }}>
                  <span><strong style={{ color: statusColor(status().connected) }}>
                    ● {status().connected ? 'CONNECTED' : 'DISCONNECTED'}
                  </strong></span>
                  <span>Gateway: {status().gatewayStatus ?? '—'}</span>
                  <span>Ping: {status().ping != null ? status().ping + 'ms' : '—'}</span>
                  <Show when={status().guild}>
                    <span>Guild: <strong>{status().guild.name}</strong> ({status().guild.memberCount})</span>
                  </Show>
                  <Show when={status().botUser}>
                    <span>Bot: <code>{status().botUser.tag}</code></span>
                  </Show>
                </div>
                <Show when={channels().length}>
                  <details style={{ 'margin-top': '0.4rem' }}>
                    <summary style={{ cursor: 'pointer', 'font-size': 'var(--fs-xs)',
                      color: 'var(--t-dim)' }}>
                      Channels ({channels().length})
                    </summary>
                    <table class="data-table" style={{ 'font-size': 'var(--fs-xs)',
                      'margin-top': '0.25rem' }}>
                      <thead><tr><th>Channel</th><th>Status</th><th>Resolved name</th></tr></thead>
                      <tbody>
                        <For each={channels()}>{([key, ch]) => (
                          <tr>
                            <td class="text-mono">{key}</td>
                            <td style={{ color: ch.found ? 'var(--fissal, #6cf)' : 'var(--bad, #ff6b6b)' }}>
                              {ch.found ? '● Found' : '✕ Missing'}
                            </td>
                            <td class="text-mono">{ch.name || '—'}</td>
                          </tr>
                        )}</For>
                      </tbody>
                    </table>
                  </details>
                </Show>
              </Show>
            </div>

            {/* Command tester */}
            <div style={{ 'margin-bottom': '0.5rem' }}>
              <div style={{ 'font-weight': '600', 'margin-bottom': '0.25rem' }}>
                Slash-command Tester
                <span style={{ color: 'var(--t-dim)', 'font-weight': 'normal',
                  'font-size': 'var(--fs-xs)', 'margin-left': '0.5rem' }}>
                  · {commands().length} registered
                </span>
              </div>
              <input
                type="text"
                class="form-input"
                placeholder="Filter commands…"
                value={filter()}
                onInput={e => setFilter(e.target.value)}
                style={{ width: '100%', 'margin-bottom': '0.4rem' }}
              />
              <div style={{ display: 'flex', 'flex-wrap': 'wrap', gap: '0.4rem',
                'align-items': 'center' }}>
                <select
                  class="form-input"
                  value={selected()}
                  onInput={e => { setSelected(e.target.value); setResult(null); }}
                  style={{ 'min-width': '200px' }}
                >
                  <option value="">Select a command…</option>
                  <For each={filteredCmds()}>{c => (
                    <option value={c.name}>
                      .{c.name}{c.adminOnly ? ' 🔒' : ''}
                    </option>
                  )}</For>
                </select>
                <input
                  type="text"
                  class="form-input"
                  placeholder="args (e.g. @Player)"
                  value={args()}
                  onInput={e => setArgs(e.target.value)}
                  style={{ 'min-width': '180px', flex: '1 1 200px' }}
                />
                <label style={{ 'font-size': 'var(--fs-xs)', display: 'flex',
                  'align-items': 'center', gap: '0.25rem' }}>
                  <input type="checkbox" checked={debug()}
                    onChange={e => setDebug(e.target.checked)} />
                  Debug
                </label>
                <button class="btn btn-primary"
                  disabled={running() || !selected()}
                  onClick={runCommand}>
                  {running() ? 'Running…' : '▶ Run'}
                </button>
              </div>

              <Show when={selectedMeta()}>
                <div style={{
                  'margin-top': '0.4rem',
                  padding: '0.4rem 0.5rem',
                  background: 'rgba(0,0,0,0.25)',
                  'border-left': '3px solid var(--t-cyan, #6cf)',
                  'border-radius': 'var(--radius, 6px)',
                  'font-size': 'var(--fs-xs)',
                }}>
                  <div>
                    <strong>.{selectedMeta().name}</strong>
                    <Show when={selectedMeta().adminOnly}>
                      <span style={{ color: 'var(--gold, #ffc857)', 'margin-left': '0.4rem' }}>
                        🔒 admin
                      </span>
                    </Show>
                    <Show when={selectedMeta().description}>
                      <span style={{ color: 'var(--t-secondary)', 'margin-left': '0.4rem' }}>
                        — {selectedMeta().description}
                      </span>
                    </Show>
                  </div>
                  <Show when={selectedMeta().usage}>
                    <div style={{ color: 'var(--t-dim)', 'margin-top': '0.2rem' }}>
                      <strong>Usage:</strong>{' '}
                      <code style={{ color: 'var(--t-cyan)' }}>{selectedMeta().usage}</code>
                    </div>
                  </Show>
                  <Show when={(selectedMeta().examples || []).length}>
                    <div style={{ 'margin-top': '0.2rem' }}>
                      <strong>Examples:</strong>{' '}
                      <For each={selectedMeta().examples}>{ex => (
                        <code style={{
                          background: 'rgba(0,0,0,0.35)',
                          padding: '1px 5px',
                          'border-radius': '3px',
                          'margin-right': '0.3rem',
                        }}>{ex}</code>
                      )}</For>
                    </div>
                  </Show>
                </div>
              </Show>

              <Show when={result()}>
                <pre style={{
                  'margin-top': '0.4rem',
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid var(--screen-border, rgba(255,255,255,0.08))',
                  'border-radius': 'var(--radius, 6px)',
                  padding: '0.5rem 0.6rem',
                  'max-height': '300px',
                  overflow: 'auto',
                  'font-family': 'var(--font-mono)',
                  'font-size': 'var(--fs-xs)',
                  'white-space': 'pre-wrap',
                  'word-break': 'break-word',
                  color: 'var(--t-white)',
                }}>{JSON.stringify(result(), null, 2)}</pre>
              </Show>
            </div>

          </div>
        </Show>
      </Show>
    </div>
  );
}
